/* eslint-disable react-hooks/refs */
'use client';

import { type RefObject, useRef } from 'react';
import { createPortal } from 'react-dom';

import { Backdrop } from '@/app/components/overlays/backdrop';
import { useAutoFocus } from '@/app/components/overlays/hooks/useAutoFocus';
import { useClickOutside } from '@/app/components/overlays/hooks/useClickOutside';
import { useEscapeKey } from '@/app/components/overlays/hooks/useEscapeKey';
import { useFocusTrap } from '@/app/components/overlays/hooks/useFocusTrap';
import { useOverlayStackStatus } from '@/app/components/overlays/hooks/useOverlayStackStatus';
import { useScrollLock } from '@/app/components/overlays/hooks/useScrollLock';
import type { OverlayProps } from '@/app/components/overlays/overlay/types';
import { isBrowser } from '@/app/utils/browser';
import { cn } from '@/app/utils/cn';
import { createDataAttributes } from '@/app/utils/createDataAttributes';

export const Overlay = ({
  overlayKey,
  instanceId,
  isOpen,
  onClose,
  usePortal = true,
  overlayClassName,
  hasBackdrop = true,
  backdropBlur = 'none',
  backdropClassName,
  backdropProps,
  transitionState,
  autoScrollLock = true,
  canEscapeKeyClose = true,
  canOutsideClickClose = true,
  autoFocus = true,
  enforceFocus = true,
  children,
  ignoreClickTargetRef,
  clickOutsideContainerRef,
  ...props
}: OverlayProps) => {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const startGuardRef = useRef<HTMLDivElement | null>(null);
  const endGuardRef = useRef<HTMLDivElement | null>(null);

  const isTop = useOverlayStackStatus({ overlayKey, instanceId });
  const isExiting = transitionState === 'exit';
  const active = isOpen && isTop && !isExiting;
  const shouldContainFocus =
    isTop &&
    (isOpen || transitionState === 'animate' || transitionState === 'exit');

  // Keep scroll locked through every mounted transition state.
  useScrollLock({
    overlayKey,
    instanceId,
    enabled:
      usePortal &&
      (isOpen || transitionState === 'animate' || transitionState === 'exit'),
    lockScroll: autoScrollLock,
  });

  // Escape key closes topmost overlay
  useEscapeKey({
    enabled: active && canEscapeKeyClose,
    onClose,
  });

  useClickOutside({
    containerRef: clickOutsideContainerRef ?? containerRef,
    enabled: active && canOutsideClickClose,
    onClickOutside: (event) => {
      if (
        ignoreClickTargetRef?.current &&
        event.target === ignoreClickTargetRef.current
      ) {
        return;
      }
      onClose();
    },
  });

  useAutoFocus({
    containerRef,
    enabled: active && autoFocus,
  });

  useFocusTrap({
    containerRef,
    startGuardRef,
    endGuardRef,
    enabled: shouldContainFocus && enforceFocus,
  });

  const renderFocusGuard = (ref: RefObject<HTMLDivElement | null>) => (
    <div ref={ref} tabIndex={0} aria-hidden="true" />
  );

  const overlayRoot = (
    <div
      ref={containerRef}
      {...props}
      tabIndex={-1}
      data-transition-state={transitionState}
      className={cn(
        'inset-0 isolate',
        {
          'fixed z-60 overflow-hidden': hasBackdrop && usePortal,
          'absolute z-60': hasBackdrop && !usePortal,
        },
        overlayClassName
      )}
      {...createDataAttributes({
        test: 'overlay',
      })}
    >
      {!!enforceFocus && renderFocusGuard(startGuardRef)}
      {!!hasBackdrop && (
        <Backdrop
          {...backdropProps}
          aria-hidden="true"
          blur={backdropBlur}
          className={cn(
            'ease-curve transition-opacity duration-500',
            {
              fixed: usePortal,
              absolute: !usePortal,
              'pointer-events-none opacity-0':
                transitionState === 'initial' || transitionState === 'exit',
              'opacity-100': transitionState === 'animate',
            },
            backdropClassName
          )}
          disabledAnimate
          onClick={
            canOutsideClickClose && !clickOutsideContainerRef
              ? onClose
              : undefined
          }
        />
      )}
      {children}
      {!!enforceFocus && renderFocusGuard(endGuardRef)}
    </div>
  );

  return usePortal && isBrowser
    ? createPortal(overlayRoot, document.body)
    : overlayRoot;
};
